import { usePostHogCapture } from '@app/core/integrations/posthog/usePostHogCapture';
import { AuditLiveMentionConfidence } from '@mi-front/core';
import { useCallback } from 'react';

/** Spot match vs live mention marker on the stream focus. */
export type AuditMarkerKind = 'spot' | 'mention';

/** User actions on audit focus-marker popups. */
export type AuditMarkerAction =
  | 'play'
  | 'pause'
  | 'copy_link'
  | 'show_mention'
  | 'confidence_changed';

export interface AuditMarkerCaptureProps {
  action: AuditMarkerAction;
  kind: AuditMarkerKind;
  /** Mention/match id when available. */
  marker_id?: string;
  /** Previous confidence when accepting/rejecting a mention. */
  from_confidence?: AuditLiveMentionConfidence;
  /** New confidence after accept/reject. */
  to_confidence?: AuditLiveMentionConfidence;
}

/**
 * PostHog capture for audit spot/mention marker popups on the logger stream.
 * Events are sent as `logger:audit_marker_action`.
 */
export function useAuditMarkerCapture() {
  const capture = usePostHogCapture({ domain: 'logger' });

  return useCallback(
    ({ action, kind, marker_id, from_confidence, to_confidence }: AuditMarkerCaptureProps) => {
      capture('audit_marker_action', {
        action,
        kind,
        ...(marker_id != null ? { marker_id } : {}),
        ...(from_confidence != null ? { from_confidence } : {}),
        ...(to_confidence != null ? { to_confidence } : {}),
      });
    },
    [capture],
  );
}
